


interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface causes {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface diagnosis {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface treatmentOptions {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface preparationTips {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}




interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    // howItWorks: HowItWorks;
    // procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    // aftercareTips: ListSection;
    faqData: FAQSection;
    causes: causes;
    diagnosis: diagnosis;
    treatmentOptions: treatmentOptions;
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const maleinfertility: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} = {
    "en": {
        "url": "male-infertility",
        "meta": {
            "title": "Male Infertility Treatment in Dubai | Elite Body Home",
            "description": "Comprehensive male infertility treatments including diagnosis, hormonal therapy, surgical procedures, and assisted reproductive technologies.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/male-infertility/",
            "og": {
                "title": "Male Infertility Treatment in Dubai | Elite Body Home",
                "description": "Advanced treatments for male infertility including hormone therapy, surgery, and assisted reproductive technologies",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Male Infertility"],
        "hero": {
            "title": "Male Infertility Treatment in Dubai",
            "subtitle": "Comprehensive Fertility Solutions",
            "description": "Male infertility is a man's inability to get a female partner pregnant after a year of unprotected intercourse. It makes up around 40-50% of all infertility causes and can be due to an array of factors, from lifestyle to other medical conditions that are not being treated. The earlier you get diagnosed, the better the treatment and success at conceiving."
        },
        "causes": {
            "heading": "Causes of Male Infertility",
            "subheading": "Understanding the root causes for effective treatment",
            "items": [
                {
                    "title": "Sperm Production Problems",
                    "desc": "Conditions like varicocele, infections, or genetic disorders may affect sperm production.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "Sperm Transport Issues",
                    "desc": "Blockages in the tubes that move the sperm from the testicles can cause infertility.",
                    "icon": "fa-solid fa-tube"
                },
                {
                    "title": "Hormonal Imbalances",
                    "desc": "Low testosterone levels or other hormone-related disorders can impair sperm production.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "Lifestyle Factors",
                    "desc": "Smoking, excessive alcohol, obesity, and exposure to toxins can affect fertility.",
                    "icon": "fa-solid fa-wine-bottle"
                },
                {
                    "title": "Sexual Dysfunction",
                    "desc": "Erectile dysfunction or problems with ejaculation can prevent successful conception.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "diagnosis": {
            "heading": "Diagnosis of Male Infertility",
            "subheading": "Comprehensive testing for accurate results",
            "items": [
                {
                    "title": "Semen Analysis",
                    "desc": "Basic test that assesses sperm count, motility, and morphology.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "Hormone Testing",
                    "desc": "Blood tests to check testosterone and other hormone levels.",
                    "icon": "fa-solid fa-vial"
                },
                {
                    "title": "Genetic Tests",
                    "desc": "Used to find genetic abnormalities that may impact fertility.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "Testicular Biopsy",
                    "desc": "Done when problems with sperm production are suspected.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Imaging Tests",
                    "desc": "Ultrasound may be used to detect blockages or abnormalities in the reproductive organs.",
                    "icon": "fa-solid fa-image"
                }
            ]
        },
        "treatmentOptions": {
            "heading": "Treatment Options for Male Infertility",
            "subheading": "Personalized solutions for your fertility needs",
            "items": [
                {
                    "title": "Medications",
                    "desc": "Hormonal therapies can restore balance, and antibiotics can treat infections that interfere with fertility.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Lifestyle Changes",
                    "desc": "Losing weight, quitting smoking, drinking less alcohol and reducing stress can improve fertility.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Surgical Procedures",
                    "desc": "Surgical correction of blockages, varicocele, or structural abnormalities.",
                    "icon": "fa-solid fa-scalpel"
                },
                {
                    "title": "Assisted Reproductive Technologies (ART)",
                    "desc": "Intrauterine insemination (IUI) or in vitro fertilization (IVF) may be recommended.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "Sperm Retrieval Techniques",
                    "desc": "For men with low sperm counts, sperm can be directly extracted from the testicles.",
                    "icon": "fa-solid fa-syringe"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Male Infertility Treatment",
            "subheading": "Comprehensive care for better outcomes",
            "items": [
                {
                    "title": "Improved Sperm Quality",
                    "desc": "Treatments can improve sperm count and motility.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Higher Conception Rates",
                    "desc": "ART techniques can boost chances of pregnancy even with low sperm counts.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "Enhanced Overall Health",
                    "desc": "Addressing lifestyle factors improves both fertility and general well-being.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Emotional Relief",
                    "desc": "Getting a diagnosis and treatment plan can alleviate stress and offer hope.",
                    "icon": "fa-solid fa-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "Who is Eligible for Treatment?",
            "subheading": "Personalized assessment for optimal results",
            "items": [
                {
                    "title": "Cause of Infertility",
                    "desc": "Treatment recommendations based on hormonal, genetic, or physical causes.",
                    "icon": "fa-solid fa-search"
                },
                {
                    "title": "Age and Health",
                    "desc": "The man's age and underlying medical conditions influence treatment decisions.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "Duration of Infertility",
                    "desc": "How long the couple has been trying to conceive may affect treatment urgency.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Partner's Fertility Health",
                    "desc": "Female fertility issues may also need to be addressed in treatment.",
                    "icon": "fa-solid fa-venus"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation for Treatment",
            "subheading": "Steps to optimize treatment success",
            "items": [
                {
                    "title": "Medical History Review",
                    "desc": "Provide details about past illnesses, surgeries and medications.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Semen Analysis",
                    "desc": "Important diagnostic test for assessing fertility.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "Blood Tests",
                    "desc": "Check hormone levels and overall health markers.",
                    "icon": "fa-solid fa-vial"
                },
                {
                    "title": "Healthy Lifestyle",
                    "desc": "Balanced diet, regular exercise and avoiding substance abuse improve success rates.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "Realistic outcomes based on treatment",
            "items": [
                {
                    "title": "Improved Sperm Count",
                    "desc": "Greater sperm counts and improved sperm quality can result from medications and lifestyle changes.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Higher Conception Rates",
                    "desc": "Low sperm count men can also achieve conception with ART techniques.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "Healthier Lifestyle",
                    "desc": "Treating underlying conditions or making healthy changes boosts overall health.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "items": [
                {
                    "question": "How common is male infertility?",
                    "answer": "Male infertility makes up around 40-50% of all infertility cases, affecting about 1 in 20 men."
                },
                {
                    "question": "Can lifestyle changes really improve fertility?",
                    "answer": "Yes, factors like quitting smoking, reducing alcohol, maintaining healthy weight, and managing stress can significantly improve fertility."
                },
                {
                    "question": "How long does treatment typically take?",
                    "answer": "Treatment duration varies based on the cause and method, ranging from a few months for lifestyle changes to longer for surgical or ART treatments."
                },
                {
                    "question": "Are fertility treatments painful?",
                    "answer": "Most treatments are minimally invasive with little discomfort. Surgical procedures may require local or general anesthesia."
                },
                {
                    "question": "What's the success rate of male infertility treatments?",
                    "answer": "Success rates vary by treatment type and individual factors, but many couples achieve pregnancy with proper treatment."
                }
            ]
        }
    },
    "ar": {
        "url": "male-infertility",
        "meta": {
            "title": "علاج العقم عند الرجال في دبي | ايليت بودي هوم",
            "description": "علاجات شاملة للعقم الذكري تشمل التشخيص والعلاج الهرموني والإجراءات الجراحية وتقنيات الإنجاب المساعدة.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/male-infertility",
            "og": {
                "title": "علاج العقم عند الرجال في دبي | ايليت بودي هوم",
                "description": "علاجات متقدمة للعقم الذكري تشمل العلاج الهرموني والجراحة وتقنيات الإنجاب المساعدة",
                "image": "https://www.elitebodyhome.com/images/male-infertility-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "العقم عند الرجال"],
        "hero": {
            "title": "علاج العقم عند الرجال في دبي",
            "subtitle": "حلول شاملة لعلاج الخصوبة",
            "description": "يشير العقم عند الرجال إلى عدم قدرة الرجل على إخصاب شريكته بعد مرور عام من العلاقة الزوجية المنتظمة دون استخدام وسائل منع الحمل. يُشكل العقم الذكري ما بين 40% إلى 50% من إجمالي حالات العقم، وقد يعود لأسباب متعددة، تشمل نمط الحياة أو حالات طبية كامنة. يُعد التشخيص المبكر والعلاج المناسب من العوامل الأساسية التي تساهم في زيادة فرص الإنجاب."
        },
        "causes": {
            "heading": "أسباب العقم عند الرجال",
            "subheading": "فهم الأسباب الجذرية للعلاج الفعال",
            "items": [
                {
                    "title": "مشكلات إنتاج الحيوانات المنوية",
                    "desc": "قد تؤثر بعض الحالات مثل دوالي الخصية، أو الالتهابات، أو العوامل الوراثية سلبًا على إنتاج الحيوانات المنوية وجودتها.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "مشكلات في حركة الحيوانات المنوية",
                    "desc": "قد تمنع الانسدادات في القنوات الناقلة من الخصيتين خروج الحيوانات المنوية، مما يؤدي إلى العقم.",
                    "icon": "fa-solid fa-tube"
                },
                {
                    "title": "اضطرابات هرمونية",
                    "desc": "قد يؤثر انخفاض مستوى هرمون التستوستيرون أو اختلال الهرمونات الأخرى بشكل مباشر على القدرة على إنتاج الحيوانات المنوية.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "عوامل متعلقة بنمط الحياة",
                    "desc": "يسبب الإفراط في التدخين أو شرب الكحول، أو السمنة، أو التعرض للمواد السامة ضعف الخصوبة.",
                    "icon": "fa-solid fa-wine-bottle"
                },
                {
                    "title": "الاضطرابات الجنسية",
                    "desc": "قد تعيق بعض الاضطرابات مثل ضعف الانتصاب أو مشكلات القذف عملية التخصيب.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "diagnosis": {
            "heading": "تشخيص العقم عند الرجال",
            "subheading": "فحوصات شاملة لنتائج دقيقة",
            "items": [
                {
                    "title": "تحليل السائل المنوي",
                    "desc": "يُعد تحليل السائل المنوي من الفحوصات الأساسية، حيث يفحص عدد الحيوانات المنوية، وحركتها، وشكلها.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "اختبارات الهرمونات",
                    "desc": "تُجرى فحوصات دم لقياس مستوى هرمون التستوستيرون والهرمونات الأخرى.",
                    "icon": "fa-solid fa-vial"
                },
                {
                    "title": "الفحوصات الچينية",
                    "desc": "تُستخدم للكشف عن أي خلل وراثي قد يؤثر على الخصوبة.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "خزعة الخصية",
                    "desc": "يتم اللجوء إليها إذا كانت هناك مؤشرات على وجود مشكلات في إنتاج الحيوانات المنوية.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "فحوصات التصوير",
                    "desc": "يمكن إجراء أشعة مثل التصوير بالأشعة فوق الصوتية، لتحديد الانسدادات أو التشوهات في الأعضاء التناسلية.",
                    "icon": "fa-solid fa-image"
                }
            ]
        },
        "treatmentOptions": {
            "heading": "خيارات علاج العقم عند الرجال",
            "subheading": "حلول مخصصة لاحتياجاتك الإنجابية",
            "items": [
                {
                    "title": "الأدوية",
                    "desc": "تشمل العلاج الهرموني لتصحيح اختلال التوازن الهرموني، والمضادات الحيوية لعلاج الالتهابات التي قد تؤثر على الخصوبة.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "تغييرات في نمط الحياة",
                    "desc": "قد يساهم فقدان الوزن، والتوقف عن التدخين، وتقليل استهلاك الكحول، والتحكم في التوتر في تحسن الخصوبة بشكل كبير.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "الإجراءات الجراحية",
                    "desc": "يتم اللجوء إلى التدخل الجراحي في بعض الحالات مثل دوالي الخصية، أو الانسدادات في القنوات الناقلة للحيوانات المنوية، أو لتصحيح التشوهات.",
                    "icon": "fa-solid fa-scalpel"
                },
                {
                    "title": "تقنيات المساعدة على الإنجاب (ART)",
                    "desc": "تُستخدم عندما يصعب حدوث الحمل بطريقة طبيعية، وتتضمن التلقيح داخل الرحم (IUI) أو التلقيح الصناعي (IVF).",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "تقنيات استخراج الحيوانات المنوية",
                    "desc": "يمكن في حالات انخفاض عدد الحيوانات المنوية أو انعدامها في السائل المنوي، استخلاصها مباشرةً من الخصية لاستخدامها في تقنيات الإخصاب.",
                    "icon": "fa-solid fa-syringe"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد علاج العقم عند الرجال",
            "subheading": "رعاية شاملة لنتائج أفضل",
            "items": [
                {
                    "title": "تحسين جودة الحيوانات المنوية",
                    "desc": "يساعد العلاج المناسب على زيادة عدد الحيوانات المنوية وتحسين حركتها.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "رفع احتمالية حدوث الحمل",
                    "desc": "يمكن لتقنيات المساعدة على الإنجاب تعزيز فرص الحمل بشكل كبير، حتى في حالات انخفاض عدد الحيوانات المنوية.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "تعزيز الصحة العامة",
                    "desc": "لا يؤثر معالجة العوامل المرتبطة بنمط الحياة مثل السمنة أو التدخين على الخصوبة فقط، بل يُحسن من الصحة العامة والنشاط الجسدي.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "تخفيف الضغط النفسي",
                    "desc": "يساعد الحصول على تشخيص دقيق وخطة علاج واضحة على تقليل القلق ويوفر الأمل للأزواج الساعين للإنجاب.",
                    "icon": "fa-solid fa-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "من المؤهل للعلاج؟",
            "subheading": "تقييم شخصي لنتائج مثالية",
            "items": [
                {
                    "title": "سبب العقم",
                    "desc": "سواء كان السبب هرمونيًا أو چينيًا أو ناتجًا عن مشكلة عضوية، فإنه يُحدد نوع العلاج الأنسب.",
                    "icon": "fa-solid fa-search"
                },
                {
                    "title": "العمر والحالة الصحية العامة",
                    "desc": "يلعب عمر الرجل وحالته الصحية العامة دورًا في اختيار العلاج وتحديد فرص نجاحه.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "مدة محاولة الإنجاب",
                    "desc": "كلما طالت مدة المحاولات غير الناجحة، زادت الحاجة إلى التدخل السريع وفاعلية الخطة العلاجية.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "صحة الشريكة الإنجابية",
                    "desc": "يُنصح في بعض الحالات بتقييم الخصوبة لدى الشريكة أيضًا، لضمان وضع خطة علاجية متكاملة للطرفين.",
                    "icon": "fa-solid fa-venus"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد للعلاج",
            "subheading": "خطوات لتحسين فرص نجاح العلاج",
            "items": [
                {
                    "title": "مراجعة التاريخ الطبي",
                    "desc": "استعد لمناقشة أي أمراض سابقة، أو عمليات جراحية، أو أدوية تتناولها حاليًا أو في الماضي.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "تحليل السائل المنوي",
                    "desc": "يُعد من التحاليل الأساسية لتقييم عدد الحيوانات المنوية وحركتها وشكلها.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "فحوصات الدم",
                    "desc": "تُجرى لفحص مستوى الهرمونات والتأكد من التوازن الهرموني قبل بدء العلاج.",
                    "icon": "fa-solid fa-vial"
                },
                {
                    "title": "نمط حياة صحي",
                    "desc": "يساهم اتباع نظام غذائي متوازن، وممارسة النشاط البدني بانتظام، والامتناع عن التدخين والكحول بشكل كبير في تحسين فرص نجاح العلاج.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "نتائج واقعية بناءً على نوع العلاج",
            "items": [
                {
                    "title": "تحسن في عدد وجودة الحيوانات المنوية",
                    "desc": "يمكن أن تُؤدي الأدوية وتعديلات نمط الحياة إلى زيادة عدد الحيوانات المنوية وتحسين جودتها.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "ارتفاع معدلات الحمل",
                    "desc": "يمكن للتقنيات الحديثة أن تُمكّن من تحقيق الحمل بنجاح حتى في حالات العقم الشديد.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "اتباع نمط حياة صحي",
                    "desc": "معالجة العوامل الصحية المُرتبطة بالعقم تُحسن من الخصوبة وتعزز الصحة العامة للرجل.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "items": [
                {
                    "question": "ما مدى انتشار العقم عند الرجال؟",
                    "answer": "يمثل العقم الذكري حوالي 40-50% من إجمالي حالات العقم، ويؤثر على حوالي 1 من كل 20 رجلاً."
                },
                {
                    "question": "هل يمكن لتغييرات نمط الحياة أن تحسن الخصوبة حقًا؟",
                    "answer": "نعم، يمكن لعوامل مثل الإقلاع عن التدخين، وتقليل الكحول، والحفاظ على وزن صحي، وإدارة الإجهاد أن تحسن الخصوبة بشكل كبير."
                },
                {
                    "question": "كم تستغرق فترة العلاج عادةً؟",
                    "answer": "تختلف مدة العلاج بناءً على السبب والطريقة، تتراوح من بضعة أشهر لتغييرات نمط الحياة إلى فترات أطول للعلاجات الجراحية أو تقنيات الإنجاب المساعدة."
                },
                {
                    "question": "هل علاجات الخصوبة مؤلمة؟",
                    "answer": "معظم العلاجات طفيفة التوغل مع القليل من الانزعاج. قد تتطلب الإجراءات الجراحية تخديرًا موضعيًا أو عامًا."
                },
                {
                    "question": "ما هي نسبة نجاح علاجات العقم عند الرجال؟",
                    "answer": "تختلف معدلات النجاح حسب نوع العلاج والعوامل الفردية، لكن العديد من الأزواج يحققون الحمل بالعلاج المناسب."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/segsg-scaled.jpg",
            "beforeAfter": [
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/105.webp",
                    "alt": "Before After Image 105"
                },
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/106.webp",
                    "alt": "Before After Image 106"
                },
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/107.webp",
                    "alt": "Before After Image 107"
                }
            ]
        },
        "product": {
            "id": 8,
            "title": "Male Infertility Treatment",
            "description": "Comprehensive solutions for male infertility including diagnosis, hormonal therapy, surgical procedures, and assisted reproductive technologies.",
            "price": 499
        }
    }
}